import { useEffect, useRef, useState } from "react";
import { setTransition } from "../router";
import { AvatarBare, useRunSheet, type Equipped } from "./Avatar";
import { calm, Sparkles } from "./fx";

/**
 * Page transition: when somebody presses a link or a start button, their own figure runs across the screen on a soft colour wash.
 * The page is switched while the wash is fully opaque (so there is no flash) and the wash fades away behind the runner.
 *
 *   0 ms      wash fades in, the figure enters from the left
 *   420 ms    screen covered → the route changes underneath
 *   1000 ms   the figure has left on the right, the new page is visible
 *
 * Safety: the route ALWAYS changes (timers, not animation events), a second press while it runs goes straight to the newest target,
 * reduced motion / "edu-run = off" / a hidden tab skip the animation, and unmounting finishes a pending move at once.
 */
export const RUN_MS = 1000;
export const COMMIT_MS = 420;
export const RUN_KEY = "edu-run";

export const runEnabled = (): boolean => {
  try { return localStorage.getItem(RUN_KEY) !== "off"; } catch { return true; }
};
export const setRunEnabled = (on: boolean) => {
  try { if (on) localStorage.removeItem(RUN_KEY); else localStorage.setItem(RUN_KEY, "off"); } catch { /* private mode: the setting just lasts until reload */ }
};

function Sprite({ sheet }: { sheet: { url: string; frames: number; px: number } }) {
  const [f, setF] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setF((x) => (x + 1) % sheet.frames), 78);
    return () => clearInterval(id);
  }, [sheet]);
  return <span className="sprite" style={{ backgroundImage: `url("${sheet.url}")`, backgroundSize: `${sheet.frames * 100}% 100%`, backgroundPosition: `${(100 * f) / Math.max(1, sheet.frames - 1)}% 0` }} />;
}

export function RunTransition({ equipped }: { equipped: Equipped | null | undefined }) {
  const sheet = useRunSheet(equipped);
  const [run, setRun] = useState<number | null>(null);
  const live = useRef<{ commit: () => void; t1: any; t2: any } | null>(null);
  const counter = useRef(0);

  useEffect(() => {
    setTransition((_to, commit) => {
      if (!runEnabled() || calm() || (typeof document !== "undefined" && document.hidden)) return false;
      const cur = live.current;
      if (cur) {
        // pressed again while running: forget the old target, `go()` navigates to the newest one right away
        clearTimeout(cur.t1);
        cur.commit = () => {};
        return false;
      }
      const l = { commit, t1: 0 as any, t2: 0 as any };
      l.t1 = setTimeout(() => { l.commit(); l.commit = () => {}; }, COMMIT_MS);
      l.t2 = setTimeout(() => { if (live.current === l) live.current = null; setRun(null); }, RUN_MS + 80);
      live.current = l;
      setRun(++counter.current);
      return true;
    });
    return () => {
      setTransition(null);
      const l = live.current;
      if (l) { clearTimeout(l.t1); clearTimeout(l.t2); l.commit(); live.current = null; }
    };
  }, []);

  if (run === null) return null;
  return (
    <div className="runfx" aria-hidden="true" key={run}>
      <div className="wash"><Sparkles n={30} /></div>
      <div className="road" />
      <div className={`runner ${sheet ? "sheet" : "flat"}`}>
        <span className="speed"><i /><i /><i /></span>
        <span className="dust"><i /><i /><i /><i /></span>
        <span className="shadow" />
        {sheet ? <Sprite sheet={sheet} /> : <><span className="flatfig"><AvatarBare equipped={equipped} size={256} /></span><span className="feet"><i /><i /></span></>}
      </div>
    </div>
  );
}
